/**
 * WebSocket Service for Real-time Notifications
 *
 * Connects to the Moya Notification Service for real-time updates.
 */

import type { Notification } from '../types';

type NotificationHandler = (notification: Notification) => void;
type CountHandler = (count: number) => void;
type ConnectionHandler = (connected: boolean) => void;
type ReadHandler = (notificationId: string) => void;

interface WebSocketMessage {
  type: string;
  notification?: Notification;
  notification_id?: string;
  count?: number;
  user_id?: string;
  timestamp?: string;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string = '';
  private token: string = '';
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectDelay: number = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingInterval: number = 25000; // 25 seconds, server pings at 30s

  // Event handlers
  private notificationHandlers: Set<NotificationHandler> = new Set();
  private countHandlers: Set<CountHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private readHandlers: Set<ReadHandler> = new Set();

  /**
   * Configure the WebSocket service.
   * @param wsUrl - WebSocket URL (notification service URL)
   * @param token - JWT authentication token
   */
  configure(wsUrl: string, token?: string) {
    // Convert HTTP URL to WebSocket URL if needed
    let url = wsUrl.replace(/^http/, 'ws');

    // Ensure we connect to the notifications endpoint
    if (!url.includes('/ws/notifications')) {
      url = url.replace(/\/?$/, '/ws/notifications');
    }

    const urlChanged = this.url !== '' && this.url !== url;
    this.url = url;
    if (token) {
      this.token = token;
    }

    // Re-pointing at a different service has to drop the socket that is open
    // (or opening) against the old one -- connect() below refuses to act
    // while a socket exists, so without this the new URL would never be used.
    if (urlChanged && this.ws) {
      this.disconnect();
    }
  }

  /**
   * Update the authentication token.
   * Reconnects if already connected.
   */
  setToken(token: string) {
    this.token = token;
    // Reconnect with new token if already connected
    if (this.ws) {
      this.disconnect();
      this.connect();
    }
  }

  /**
   * Connect to the notification service.
   */
  connect() {
    // CONNECTING counts as connected-enough. This guard used to check OPEN
    // only, so two callers in the same tick -- the header's initialize() and
    // the bell's setupWebSocket(), which is exactly what happens once both
    // are given a URL -- each opened a socket, and the first was orphaned
    // with its reconnect timer still running.
    if (
      this.ws?.readyState === WebSocket.OPEN ||
      this.ws?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    if (!this.url) {
      console.warn('[MoyaWS] No WebSocket URL configured');
      return;
    }

    if (!this.token) {
      console.warn('[MoyaWS] No authentication token configured');
      return;
    }

    try {
      const wsUrl = `${this.url}?token=${encodeURIComponent(this.token)}`;

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[MoyaWS] Connected to notification service');
        this.reconnectAttempts = 0;
        this.notifyConnectionHandlers(true);
        this.startPing();
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (e) {
          console.error('[MoyaWS] Failed to parse message:', e);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[MoyaWS] WebSocket error:', error);
      };

      this.ws.onclose = (event) => {
        console.log('[MoyaWS] Disconnected:', event.code, event.reason);
        this.stopPing();
        this.notifyConnectionHandlers(false);

        // Don't reconnect if closed intentionally (code 1000) or auth failed (4001, 4002)
        if (event.code !== 1000 && event.code !== 4001 && event.code !== 4002) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('[MoyaWS] Failed to connect:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from the notification service.
   */
  disconnect() {
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  /**
   * Send a message to the server.
   */
  send(message: object) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private handleMessage(message: WebSocketMessage) {
    switch (message.type) {
      case 'connected':
        // Initial connection acknowledgment
        console.log(`[MoyaWS] Connected as user ${message.user_id}`);
        break;

      case 'ping':
        // Server heartbeat, respond with pong
        this.send({ type: 'pong' });
        break;

      case 'notification':
        // New notification received
        if (message.notification) {
          this.notifyNotificationHandlers(message.notification);
        }
        break;

      case 'notification_read':
        // A notification was marked as read
        if (message.notification_id) {
          this.notifyReadHandlers(message.notification_id);
        }
        break;

      case 'notification_dismissed':
        // A notification was dismissed
        if (message.notification_id) {
          this.notifyReadHandlers(message.notification_id);
        }
        break;

      case 'all_notifications_read':
        // All notifications marked as read
        if (typeof message.count === 'number') {
          this.notifyCountHandlers(0); // All read means 0 unread
        }
        break;

      default:
        console.log('[MoyaWS] Unknown message type:', message.type);
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('[MoyaWS] Max reconnect attempts reached');
      return;
    }

    // Exponential backoff with jitter
    const baseDelay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    const jitter = Math.random() * 1000;
    const delay = Math.min(baseDelay + jitter, 30000); // Max 30 seconds

    this.reconnectAttempts++;

    console.log(`[MoyaWS] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private startPing() {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'pong' }); // Respond to anticipated server ping
      }
    }, this.pingInterval);
  }

  private stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  // Event subscription methods

  /**
   * Subscribe to new notification events.
   * Returns an unsubscribe function.
   */
  onNotification(handler: NotificationHandler): () => void {
    this.notificationHandlers.add(handler);
    return () => this.notificationHandlers.delete(handler);
  }

  /**
   * Subscribe to unread count updates.
   * Returns an unsubscribe function.
   */
  onUnreadCount(handler: CountHandler): () => void {
    this.countHandlers.add(handler);
    return () => this.countHandlers.delete(handler);
  }

  /**
   * Subscribe to connection state changes.
   * Returns an unsubscribe function.
   */
  onConnectionChange(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  /**
   * Subscribe to notification read events.
   * Returns an unsubscribe function.
   */
  onNotificationRead(handler: ReadHandler): () => void {
    this.readHandlers.add(handler);
    return () => this.readHandlers.delete(handler);
  }

  private notifyNotificationHandlers(notification: Notification) {
    this.notificationHandlers.forEach((handler) => {
      try {
        handler(notification);
      } catch (e) {
        console.error('[MoyaWS] Notification handler error:', e);
      }
    });
  }

  private notifyCountHandlers(count: number) {
    this.countHandlers.forEach((handler) => {
      try {
        handler(count);
      } catch (e) {
        console.error('[MoyaWS] Count handler error:', e);
      }
    });
  }

  private notifyConnectionHandlers(connected: boolean) {
    this.connectionHandlers.forEach((handler) => {
      try {
        handler(connected);
      } catch (e) {
        console.error('[MoyaWS] Connection handler error:', e);
      }
    });
  }

  private notifyReadHandlers(notificationId: string) {
    this.readHandlers.forEach((handler) => {
      try {
        handler(notificationId);
      } catch (e) {
        console.error('[MoyaWS] Read handler error:', e);
      }
    });
  }

  /**
   * Check if currently connected.
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get current connection state.
   */
  get connectionState(): string {
    if (!this.ws) return 'disconnected';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'closed';
      default:
        return 'unknown';
    }
  }
}

// Export singleton instance
export const wsService = new WebSocketService();

export default wsService;
